import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Contra.css';

const EMPTY = { entry_date: today(), from: 'Cash', to: '', amount: '', narration: '' };

export default function Contra() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: entries, loading, error, reload } = useApi(() => api.contra.list(), []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = banksData.banks || [];

  const handleSave = async () => {
    if (!form.to) { notify('❌ ' + t('contra.errors.selectTo')); return; }
    if (form.from === form.to) { notify('❌ ' + t('contra.errors.fromToSame')); return; }
    const amt = parseFloat(form.amount || 0);
    if (amt <= 0) { notify('❌ ' + t('contra.errors.amountPositive')); return; }

    setSaving(true);
    try {
      await api.contra.create({
        entry_date: form.entry_date,
        from:       form.from,
        to:         form.to,
        amount:     amt,
        narration:  form.narration.trim(),
      });
      notify('✅ ' + t('contra.toasts.saved'));
      setForm(EMPTY);
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.contra.remove(confirm.id);
      notify('✅ ' + t('contra.toasts.deleted'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const accountLabel = (type, name) => {
    if (type === 'Cash') return '💵 ' + t('common.cash');
    if (type === 'Bank') return `🏦 ${name}`;
    return name;
  };

  return (
    <div className="ct-page">
      <div className="page-header">
        <h2 className="page-title">{t('contra.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {t('contra.subtitle')}
        </span>
      </div>

      {msg && <div className="ct-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      <div className="section-card">
        <div className="section-header">{t('contra.newContra')}</div>
        <div className="ct-form-grid">
          <div className="form-group">
            <label>{t('common.date')} *</label>
            <input type="date" value={form.entry_date}
                   onChange={e => setForm({ ...form, entry_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('common.from')} *</label>
            <select value={form.from} onChange={e => setForm({ ...form, from: e.target.value })}>
              <option value="Cash">💵 {t('common.cash')}</option>
              {banks.map(b => (
                <option key={b.id} value={`Bank:${b.id}`}>🏦 {b.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('common.to')} *</label>
            <select value={form.to} onChange={e => setForm({ ...form, to: e.target.value })}>
              <option value="">{t('contra.selectOption')}</option>
              <option value="Cash">💵 {t('common.cash')}</option>
              {banks.map(b => (
                <option key={b.id} value={`Bank:${b.id}`}>🏦 {b.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('common.amount')} *</label>
            <input type="number" step="0.01" value={form.amount}
                   onChange={e => setForm({ ...form, amount: e.target.value })}
                   placeholder="0.00" />
          </div>
          <div className="form-group ct-wide">
            <label>{t('contra.narration')}</label>
            <input value={form.narration}
                   onChange={e => setForm({ ...form, narration: e.target.value })}
                   placeholder={t('contra.narrationPlaceholder')} />
          </div>
        </div>

        {/* Visual flow indicator */}
        {form.from && form.to && form.from !== form.to && (
          <div className="ct-flow-preview">
            <span>{form.from === 'Cash' ? '💵 ' + t('common.cash') : `🏦 ${banks.find(b => `Bank:${b.id}` === form.from)?.name || t('common.bank')}`}</span>
            <span className="ct-flow-arrow">→</span>
            <span>{form.to === 'Cash' ? '💵 ' + t('common.cash') : `🏦 ${banks.find(b => `Bank:${b.id}` === form.to)?.name || t('common.bank')}`}</span>
            {form.amount && <span className="ct-flow-amount">₹ {fmtNum(form.amount)}</span>}
          </div>
        )}

        <div style={{ padding: '0 12px 12px' }}>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
            {saving ? '⏳ ' + t('common.saving') + '...' : t('contra.saveEntry')}
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{t('contra.recentEntries')}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{t('contra.ref')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.from')}</th>
                <th></th>
                <th style={{ textAlign: 'left' }}>{t('common.to')}</th>
                <th>{t('common.amount')}</th>
                <th style={{ textAlign: 'left' }}>{t('contra.narration')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(entries || []).length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {t('contra.noEntries')}
                </td></tr>
              ) : (entries || []).map(e => (
                <tr key={e.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(e.entry_date)}</td>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace', fontWeight: 600 }}>{e.reference_no}</td>
                  <td style={{ textAlign: 'left' }}>{e.from_name || '—'}</td>
                  <td style={{ fontSize: 16, color: 'var(--primary)' }}>→</td>
                  <td style={{ textAlign: 'left' }}>{e.to_name || '—'}</td>
                  <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    ₹ {fmtNum(e.amount)}
                  </td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {e.narration || '—'}
                  </td>
                  <td>
                    <Can module="accounting" action="read">
                      {() => (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                                onClick={() => setConfirm(e)}>🗑</button>
                      )}
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('contra.deleteTitle')}
        message={confirm ? t('contra.deleteMessage', { ref: confirm.reference_no }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
